import { type LucideIcon } from "lucide-react";
import { type ReactNode } from "react";

import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/shared/components/primitives/empty";
import { cn } from "@/shared/lib/utils";

interface TenkoEmptyProps {
  /**
   * Lucide icon rendered in a muted square above the title. Choose an icon that reflects the missing content
   * — e.g. `<SearchX>` for empty search results, `<Inbox>` for no messages, `<FileX>` for no documents.
   */
  icon?: LucideIcon;
  /** Short heading that names what is missing. One phrase — no period. */
  title: string;
  /** Optional sentence explaining why content is absent or what the user can do. */
  description?: ReactNode;
  /**
   * Optional call-to-action rendered below the description. Typically a single `<TenkoButton>` to create the
   * first item or clear a filter.
   */
  children?: ReactNode;
  /**
   * Surface the empty state sits on. `"page"` draws the hatched page background inside its own border;
   * `"card"` inherits the card it is embedded in — `bg-empty` resolves to `var(--background)`, so inside a
   * `bg-card` panel (a `TenkoDataTable`, a section card) the default reads as a hole punched in the surface.
   *
   * @default "page"
   */
  surface?: "page" | "card";
  className?: string;
}

/**
 * Use `TenkoEmpty` to fill a page or section when there is no content to display — e.g. an empty list, zero
 * search results, or a feature the user has not set up yet.
 *
 * Pass `children` to offer a recovery action (create first item, clear filters, etc.).
 *
 * @summary for empty list, zero results, or uninitialized feature states
 */
export function TenkoEmpty({
  icon: Icon,
  title,
  description,
  children,
  surface = "page",
  className,
}: TenkoEmptyProps) {
  return (
    <Empty className={cn(surface === "card" ? "bg-card" : "bg-empty border border-border", className)}>
      <EmptyHeader>
        {Icon && (
          <EmptyMedia variant="icon">
            <Icon />
          </EmptyMedia>
        )}
        <EmptyTitle>{title}</EmptyTitle>
        {description && <EmptyDescription>{description}</EmptyDescription>}
      </EmptyHeader>
      {children && <EmptyContent>{children}</EmptyContent>}
    </Empty>
  );
}
